import { compareRecordedDates } from '@/utils/recordedValues'
/**
 * Session Store - Zustand
 * State management for Session Management
 */

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { UserSession, SessionFilters, SessionStatus } from '@/types/session.types'
import { DEFAULT_SESSION_FILTERS } from '@/types/session.types'
import { getSessionService } from '@/services/sessions'

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface SessionState {
  // Data
  sessions: UserSession[]
  currentSessionId: string | null
  isLoading: boolean
  error: string | null

  // Filters
  filters: SessionFilters

  // Stats (for admin view)
  stats: {
    totalActive: number
    byDevice: Record<string, number>
    byLocation: Array<{ city: string; count: number }>
  } | null
}

interface SessionActions {
  // Load operations
  loadUserSessions: (userId: string) => Promise<void>
  loadAllSessions: () => Promise<void>
  loadSessionStats: () => Promise<void>

  // Session management
  terminateSession: (sessionId: string) => Promise<void>
  terminateAllOtherSessions: (userId: string, currentSessionId: string) => Promise<number>

  // Current session
  setCurrentSessionId: (sessionId: string | null) => void

  // Filters
  setFilters: (filters: Partial<SessionFilters>) => void
  clearFilters: () => void

  // Computed
  getFilteredSessions: () => UserSession[]
  getActiveSessionCount: () => number

  // Reset
  resetStore: () => void
}

type SessionStore = SessionState & SessionActions

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: SessionState = {
  sessions: [],
  currentSessionId: null,
  isLoading: false,
  error: null,
  filters: DEFAULT_SESSION_FILTERS,
  stats: null,
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useSessionStore = create<SessionStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // ========================================
      // LOAD OPERATIONS
      // ========================================

      loadUserSessions: async (userId: string) => {
        set({ isLoading: true, error: null })
        try {
          const sessions = await getSessionService().listForUser(userId)
          set({ sessions, isLoading: false })
        } catch (err) {
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : 'Failed to load sessions',
          })
        }
      },

      loadAllSessions: async () => {
        set({ isLoading: true, error: null })
        try {
          const sessions = await getSessionService().listAllActive()
          set({ sessions, isLoading: false })
        } catch (err) {
          set({
            isLoading: false,
            error: err instanceof Error ? err.message : 'Failed to load sessions',
          })
        }
      },

      loadSessionStats: async () => {
        const stats = await getSessionService().getStats()
        set({ stats })
      },

      // ========================================
      // SESSION MANAGEMENT
      // ========================================

      terminateSession: async (sessionId: string) => {
        set({ isLoading: true, error: null })

        const success = await getSessionService().terminate(sessionId)

        if (success) {
          set(state => ({
            sessions: state.sessions.map(s =>
              s.sessionId === sessionId
                ? {
                    ...s,
                    status: 'revoked' as SessionStatus,
                    loggedOutAt: new Date().toISOString(),
                  }
                : s
            ),
            isLoading: false,
          }))
        } else {
          set({ isLoading: false, error: 'Failed to terminate session' })
        }
      },

      terminateAllOtherSessions: async (userId: string, currentSessionId: string) => {
        set({ isLoading: true, error: null })

        const count = await getSessionService().terminateAllOther(userId, currentSessionId)

        set(state => ({
          sessions: state.sessions.map(s =>
            s.userId === userId && s.sessionId !== currentSessionId && s.status === 'active'
              ? { ...s, status: 'revoked' as SessionStatus, loggedOutAt: new Date().toISOString() }
              : s
          ),
          isLoading: false,
        }))

        return count
      },

      // ========================================
      // CURRENT SESSION
      // ========================================

      setCurrentSessionId: (sessionId: string | null) => {
        set({ currentSessionId: sessionId })
      },

      // ========================================
      // FILTERS
      // ========================================

      setFilters: (newFilters: Partial<SessionFilters>) => {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
        }))
      },

      clearFilters: () => {
        set({ filters: DEFAULT_SESSION_FILTERS })
      },

      // ========================================
      // COMPUTED
      // ========================================

      getFilteredSessions: () => {
        const { sessions, filters } = get()

        let filtered = [...sessions]

        // Filter by status
        if (filters.status && filters.status !== 'all') {
          filtered = filtered.filter(s => s.status === filters.status)
        }

        // Filter by device type
        if (filters.deviceType && filters.deviceType !== 'all') {
          filtered = filtered.filter(s => s.deviceType === filters.deviceType)
        }

        // Filter by user
        if (filters.userId) {
          filtered = filtered.filter(s => s.userId === filters.userId)
        }

        // Sort by last activity (most recent first)
        filtered.sort((a, b) => compareRecordedDates(a.lastActivityAt, b.lastActivityAt, 'desc'))

        return filtered
      },

      getActiveSessionCount: () => {
        return get().sessions.filter(s => s.status === 'active').length
      },

      // ========================================
      // RESET
      // ========================================

      resetStore: () => {
        set(initialState)
      },
    }),
    { name: 'SessionStore' }
  )
)

// ============================================================================
// SELECTORS
// ============================================================================

export const selectSessions = (state: SessionStore) => state.sessions
export const selectCurrentSessionId = (state: SessionStore) => state.currentSessionId
export const selectIsLoading = (state: SessionStore) => state.isLoading
export const selectFilters = (state: SessionStore) => state.filters
export const selectStats = (state: SessionStore) => state.stats
